import { LoadingOverlay, Text, Tooltip } from '@mantine/core';
import { Group, Menu } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconDotsVertical, IconEye, IconPencil, IconServer, IconSettings } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { FaultFixValues } from 'components/modals/fix-device-fault/FixDeviceFaultModal';
import { useFixDeviceFault } from 'components/modals/fix-device-fault/FixDeviceFaultProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import panic from 'errors/panic';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DEVICE_FAULTS_PAGE_PATH, EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Parameters of the FaultActions component.
 */
export interface FaultActionsProps {
  faultId: number;
  isFixed: boolean;
  faultName: string;
  deviceId: number;
  deviceName: string;
  department: { departmentId: number };
  compactOnTablet?: boolean;
  onResolutionSaved: () => void;
  onFaultFixed: ({
    fixedAt,
    fixedNote,
    fixedBy,
    recordedBy,
  }: {
    fixedAt: string;
    fixedNote?: string;
    fixedBy: { userId: number; firstName: string; lastName: string; fullName?: string };
    recordedBy?: { userId: number; firstName: string; lastName: string; fullName?: string };
  }) => void;
}

/**
 * Actions for the Fault table.
 */
export default function FaultActions({
  faultId,
  isFixed,
  deviceId,
  faultName,
  deviceName,
  department: { departmentId },
  compactOnTablet = false,
  onResolutionSaved,
  onFaultFixed,
}: FaultActionsProps) {
  const { faultFix } = useFixDeviceFault();
  const { getAction, roleId } = useApi();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const [opened, setOpened] = useState(false);
  const [resolutionSaved, setResolutionSaved] = useState(false);
  const [loading, setLoading] = useState(roleId !== ROLE_ADMIN_ID);
  const [canEditDevices, setCanEditDevices] = useState(roleId === ROLE_ADMIN_ID);
  const [canResolveDeviceFaults, setCanResolveDeviceFaults] = useState(roleId === ROLE_ADMIN_ID);

  const [canResolveDeviceFaultsOnBehalf, setCanResolveDeviceFaultsOnBehalf] = useState(roleId === ROLE_ADMIN_ID);

  /**
   * Handles the fault fix.
   */
  async function handleFaultFix({ fixedAt, fixedNote, fixedByUserId }: FaultFixValues) {
    await getAction('DeviceMarkFaultFixed')({
      parameters: { deviceId: String(deviceId), faultId: String(faultId) },
      payload: { fixedAt, fixedNote, fixedByUserId },
    });
    setResolutionSaved(true);
    onResolutionSaved();
    showNotification({
      title: 'Nedostatok zariadenia bol vyriešený',
      message: `Nedostatok ${faultName} zariadenia ${deviceName} bol označený ako vyriešený.`,
      color: SUCCESS_NOTIFICATION_COLOR,
    });
    try {
      const response = await getAction('DeviceListFaults')({ parameters: { deviceId: String(deviceId) } });
      const fixed = response.faults?.find((fault) => fault.faultId === faultId)?.fixed;
      if (!fixed?.fixedAt) throw new Error('Uložené riešenie sa nepodarilo načítať. Obnovte stránku.');
      onFaultFixed({ ...fixed, fixedAt: fixed.fixedAt });
    } catch (error: any) {
      // Resolution succeeded: a failed refresh must never offer another mutation.
      panic(error);
      showNotification({
        title: 'Riešenie bolo uložené',
        message: 'Zobrazenie sa nepodarilo obnoviť. Obnovte stránku.',
        color: 'orange',
      });
    }
  }

  useEffect(() => {
    if (opened) {
      setLoading(true);

      const action = getAction('AuthUserHasPermissionInDepartment');

      Promise.all([
        action({ parameters: { departmentId: String(departmentId), permissionSlug: 'manage-devices' } }),
        action({ parameters: { departmentId: String(departmentId), permissionSlug: 'resolve-device-faults' } }),
        action({
          parameters: { departmentId: String(departmentId), permissionSlug: 'resolve-device-faults-on-behalf' },
        }),
      ])
        .then(([manageDevices, resolveDeviceFaults, resolveOnBehalf]) => {
          setCanEditDevices(manageDevices.hasPermission);
          setCanResolveDeviceFaults(resolveDeviceFaults.hasPermission);
          setCanResolveDeviceFaultsOnBehalf(resolveOnBehalf.hasPermission);
        })
        .catch(panic)
        .finally(() => setLoading(false));
    }
  }, [opened]);

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          <Tooltip label="Zobraziť" events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon
              variant="secondary"
              size="md"
              component={Link}
              to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId })}
            >
              <IconEye />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button
            size="md"
            w={120}
            component={Link}
            to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId })}
            variant="secondary"
            leftIcon={<IconEye />}
          >
            Zobraziť
          </Button>
        ))}

      <Menu position="bottom-end" opened={opened} withinPortal onChange={setOpened}>
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <LoadingOverlay visible={loading} loaderProps={{ size: 12 }} />
          <Menu.Label>
            <Text maw={160} truncate>
              {faultName}
            </Text>
          </Menu.Label>
          {menuOnly && (
            <Menu.Item
              component={Link}
              to={DEVICE_FAULTS_PAGE_PATH.insert({ deviceId })}
              icon={<IconEye stroke={1.5} size={20} />}
            >
              Zobraziť
            </Menu.Item>
          )}
          {(canResolveDeviceFaults || canResolveDeviceFaultsOnBehalf) && !isFixed && !resolutionSaved && (
            <Menu.Item
              onClick={() =>
                faultFix({
                  deviceId,
                  canResolveDeviceFaults,
                  canResolveDeviceFaultsOnBehalf,
                  onFaultFix: handleFaultFix,
                })
              }
              icon={<IconSettings stroke={1.5} size={20} />}
            >
              Vyriešiť nedostatok
            </Menu.Item>
          )}
          <Menu.Item
            icon={canEditDevices ? <IconPencil stroke={1.5} size={20} /> : <IconServer stroke={1.5} size={20} />}
            component={Link}
            to={EDIT_DEVICE_PAGE_PATH.insert({ deviceId })}
          >
            {canEditDevices ? 'Editovať' : 'zobraziť'} zariadenie
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
